import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import GavelIcon from '@mui/icons-material/Gavel';
import { recusarConviteJurado, obterDadosPublicosConvite } from '../../api/jurados';
import AuthLayout from '../../layouts/Auth';
import { extrairMensagemErro } from '../../utils/apiError';

export const CONVITE_JURADO_STORAGE_KEY = 'konq.conviteJuradoId';
export const CONVITE_JURADO_EMAIL_KEY = 'konq.conviteJuradoEmail';

function StatusIcon({ color, children }) {
  return (
    <Box
      sx={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: `${color}.main`,
        bgcolor: (theme) => alpha(theme.palette[color].main, 0.1),
      }}
    >
      {children}
    </Box>
  );
}

export default function ConviteAceite() {
  const { t } = useTranslation();
  const { id: conviteJuradoId } = useParams();
  const navigate = useNavigate();

  const [carregando, setCarregando] = useState(true);
  const [convite, setConvite] = useState(null);
  const [recusandoLoading, setRecusandoLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [recusado, setRecusado] = useState(false);
  const [dialogAberto, setDialogAberto] = useState(false);

  // Busca dados do convite ao carregar
  useEffect(() => {
    const buscarConvite = async () => {
      setCarregando(true);
      setErro(null);
      try {
        const res = await obterDadosPublicosConvite(conviteJuradoId);
        const dados = res?.data?.data;

        if (!dados) {
          setErro(t('convite.naoEncontrado'));
          return;
        }

        if (dados.expirado) {
          setErro(t('convite.expirado'));
          return;
        }

        if (dados.status === 'Aceito') {
          setErro(t('convite.jaAceito'));
          return;
        }

        if (dados.status === 'Revogado') {
          setErro(t('convite.revogado'));
          return;
        }

        setConvite(dados);
      } catch (err) {
        setErro(extrairMensagemErro(err, t('convite.erroBuscar')));
      } finally {
        setCarregando(false);
      }
    };

    if (conviteJuradoId) {
      buscarConvite();
    }
  }, [conviteJuradoId, t]);

  const handleAceitar = () => {
    try {
      sessionStorage.setItem(CONVITE_JURADO_STORAGE_KEY, conviteJuradoId);
      // Salva o email para pré-preencher no cadastro
      if (convite?.email) {
        sessionStorage.setItem(CONVITE_JURADO_EMAIL_KEY, convite.email);
      }
    } catch {
      /* ignore */
    }
    navigate('/registre-se');
  };

  const handleSolicitarRecusa = () => setDialogAberto(true);

  const handleConfirmarRecusa = async () => {
    setDialogAberto(false);
    setRecusandoLoading(true);
    setErro(null);
    try {
      await recusarConviteJurado(conviteJuradoId);
      setRecusado(true);
    } catch (err) {
      setErro(extrairMensagemErro(err, t('convite.erroRecusar')));
    } finally {
      setRecusandoLoading(false);
    }
  };

  return (
    <>
      <Helmet title={t('convite.titulo')} />
      <AuthLayout>
        {carregando ? (
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <CircularProgress size={48} />
            <Typography variant="body2">{t('convite.carregando')}</Typography>
          </Stack>
        ) : recusado ? (
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <StatusIcon color="error">
              <CancelOutlinedIcon sx={{ fontSize: 36 }} />
            </StatusIcon>
            <Typography variant="h5">{t('convite.recusadoTitulo')}</Typography>
            <Typography variant="body2">{t('convite.recusadoSubtitulo')}</Typography>
          </Stack>
        ) : erro && !convite ? (
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <StatusIcon color="error">
              <CancelOutlinedIcon sx={{ fontSize: 36 }} />
            </StatusIcon>
            <Typography variant="h5">{t('convite.invalido')}</Typography>
            <Alert severity="error" sx={{ width: '100%' }}>
              {erro}
            </Alert>
          </Stack>
        ) : (
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <StatusIcon color="primary">
              <GavelIcon sx={{ fontSize: 36 }} />
            </StatusIcon>
            <Typography variant="h5">
              {convite?.nome ? t('convite.cabecalhoNomeado', { nome: convite.nome }) : t('convite.cabecalho')}
            </Typography>
            <Typography variant="body2">{t('convite.descricao')}</Typography>

            {erro && (
              <Alert severity="error" sx={{ width: '100%' }}>
                {erro}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} width="100%">
              <Button
                fullWidth
                size="large"
                variant="outlined"
                color="error"
                disabled={recusandoLoading}
                onClick={handleSolicitarRecusa}
                startIcon={recusandoLoading ? <CircularProgress size={18} color="inherit" /> : <CancelOutlinedIcon />}
              >
                {t('convite.recusar')}
              </Button>
              <Button
                fullWidth
                size="large"
                variant="contained"
                color="primary"
                onClick={handleAceitar}
                startIcon={<CheckCircleIcon />}
              >
                {t('convite.aceitar')}
              </Button>
            </Stack>
          </Stack>
        )}
      </AuthLayout>

      {/* Dialog de confirmação de recusa */}
      <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('convite.confirmarRecusaTitulo')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('convite.confirmarRecusaTexto')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogAberto(false)}>{t('convite.voltar')}</Button>
          <Button onClick={handleConfirmarRecusa} color="error" variant="contained">
            {t('convite.confirmarRecusa')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
